import type { HelpArticle } from "./types";
import { blocksToPlainText } from "@/components/legal/LegalContent";

/**
 * Lightweight client-side search over the curated article set. We
 * intentionally do *not* pull in `lunr`/`fuse.js` — the corpus is small
 * (a few dozen articles) so a token-overlap score is plenty fast and
 * keeps the bundle lean. If the corpus ever grows past a few hundred
 * articles, swap this for `flexsearch` and keep the same return shape.
 */
export interface HelpSearchHit {
  article: HelpArticle;
  score: number;
}

const STOPWORDS = new Set([
  "the","a","an","and","or","but","is","are","was","were","be","been","of","to",
  "in","on","for","with","by","at","from","as","i","my","your","you","do","does",
  "how","what","when","where","why","can","my","me","this","that","it","its",
]);

function tokenize(input: string): string[] {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9\s]+/g, " ")
    .split(/\s+/)
    .filter((t) => t && !STOPWORDS.has(t));
}

interface IndexEntry {
  article: HelpArticle;
  titleTokens: Set<string>;
  summaryTokens: Set<string>;
  bodyTokens: Set<string>;
  keywordTokens: Set<string>;
  keywordText: string;
}

let indexCache: IndexEntry[] | null = null;

function buildIndex(articles: HelpArticle[]): IndexEntry[] {
  return articles.map((article) => ({
    article,
    titleTokens: new Set(tokenize(article.title)),
    summaryTokens: new Set(tokenize(article.summary)),
    bodyTokens: new Set(tokenize(blocksToPlainText(article.body, {}))),
    // Curated synonyms so real-world phrasings land on the right article
    // without the searcher having to know the platform's own wording.
    keywordTokens: new Set((article.keywords ?? []).flatMap((k) => tokenize(k))),
    keywordText: (article.keywords ?? []).join(" ").toLowerCase(),
  }));
}

/**
 * Token-overlap search. Per token, the best single bucket counts:
 * title exact 5x, keyword exact 4x, summary 2x, body 1x — with a
 * substring fallback (title 3x, keyword 2x, summary 1x) so partial
 * typing still matches. Returns hits sorted by score desc, capped at
 * `limit`.
 */
export function searchHelp(
  articles: HelpArticle[],
  query: string,
  limit = 20,
): HelpSearchHit[] {
  const tokens = tokenize(query);
  if (tokens.length === 0) return [];

  // Rebuild the index when the article set changes by reference (HMR).
  if (!indexCache || indexCache.length !== articles.length) {
    indexCache = buildIndex(articles);
  }

  const hits: HelpSearchHit[] = [];
  for (const entry of indexCache) {
    let score = 0;
    for (const token of tokens) {
      if (entry.titleTokens.has(token)) score += 5;
      else if (entry.keywordTokens.has(token)) score += 4;
      else if (entry.summaryTokens.has(token)) score += 2;
      else if (entry.bodyTokens.has(token)) score += 1;
      // Substring match against title/keywords/summary catches partial
      // typing (e.g. "insur" → "insurance") without needing a stemmer.
      else if (entry.article.title.toLowerCase().includes(token)) score += 3;
      else if (entry.keywordText.includes(token)) score += 2;
      else if (entry.article.summary.toLowerCase().includes(token)) score += 1;
    }
    if (score > 0) hits.push({ article: entry.article, score });
  }
  hits.sort((a, b) => b.score - a.score);
  return hits.slice(0, limit);
}

/** Reset the in-memory index — exposed for tests. */
export function _resetHelpSearchIndex(): void {
  indexCache = null;
}
